import MonitorHeartRoundedIcon from '@mui/icons-material/MonitorHeartRounded';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import MedicalInformationRoundedIcon from '@mui/icons-material/MedicalInformationRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import { lazy } from 'react';
import { PERMISSIONS, holdsPermission } from '@/api/permissions';
import { paths } from '@/routes/paths';
import { RequirePermission } from '@/routes/guards';
import type { ModuleManifest } from './manifest';

// Every page is code-split: the sign-in screen should not ship the data grid.
const PatientsPage = lazy(() => import('@/features/patients/PatientsPage'));
const PatientDetailPage = lazy(() => import('@/features/patients/PatientDetailPage'));
const PatientLookupPage = lazy(() => import('@/features/patients/PatientLookupPage'));
const VisitsPage = lazy(() => import('@/features/visits/VisitsPage'));
const VisitDetailPage = lazy(() => import('@/features/visits/VisitDetailPage'));
const ConsultationPage = lazy(() => import('@/features/consultation/ConsultationPage'));
const DoctorsPage = lazy(() => import('@/features/doctors/DoctorsPage'));
const DispensesPage = lazy(() => import('@/features/pharmacy/DispensesPage'));
const VisitPrintPage = lazy(() => import('@/features/visits/print/VisitPrintPage'));
const PrescriptionPrintPage = lazy(() => import('@/features/prescriptions/print/PrescriptionPrintPage'));

/**
 * The clinic: patients, their visits and the doctors who sign them.
 *
 * Prescriptions have no browsing surface of their own (owner's call,
 * 2026-09-28): they are written on the consultation screen, read on the visit
 * that issued them, printed as the patient's sheet, and consumed at the
 * pharmacy counter. PRESCRIPTION_READ is therefore not a door into this
 * module — it opens nothing on its own — but it still guards the print route.
 * Dispensing is the pharmacy's module, not this one; a prescribing
 * organization cannot hold PRESCRIPTION_DISPENSE.
 */
export const clinicModule: ModuleManifest = {
  key: 'clinic',
  titleKey: 'modules.clinic.title',
  descriptionKey: 'modules.clinic.description',
  icon: MonitorHeartRoundedIcon,
  prefix: '/clinic',
  anyOf: [PERMISSIONS.PATIENT_READ, PERMISSIONS.VISIT_READ],
  sections: [
    {
      key: 'clinic',
      items: [
        {
          key: 'patients',
          labelKey: 'nav.patients',
          to: paths.patients,
          icon: <MonitorHeartRoundedIcon />,
          anyOf: [PERMISSIONS.PATIENT_READ],
        },
        {
          key: 'visits',
          labelKey: 'nav.visits',
          to: paths.visits,
          icon: <EventNoteRoundedIcon />,
          anyOf: [PERMISSIONS.VISIT_READ],
        },
        {
          key: 'doctors',
          labelKey: 'nav.doctors',
          to: paths.doctors,
          icon: <MedicalInformationRoundedIcon />,
          anyOf: [PERMISSIONS.DOCTOR_READ],
        },
        {
          /*
           * The fills of this clinic's prescriptions — that they were
           * collected, and how much, never where or by whom. Not a
           * prescriptions list: PRESCRIPTION_READ still opens no door into
           * this module on its own (the module's gate is unchanged).
           */
          key: 'dispenses',
          labelKey: 'dispenses.navClinic',
          to: paths.clinicDispenses,
          icon: <ReceiptLongRoundedIcon />,
          anyOf: [PERMISSIONS.PRESCRIPTION_READ],
        },
      ],
    },
  ],
  // Resolved for the viewer: the module gate is "either read", so the landing
  // must be one the viewer actually holds, never the forbidden page.
  routes: [
    {
      element: <RequirePermission permission={[PERMISSIONS.PATIENT_READ]} />,
      children: [
        { path: paths.patients, element: <PatientsPage /> },
        // The literal route has to precede the parameterised one.
        { path: paths.patientLookup, element: <PatientLookupPage /> },
        { path: paths.patient(), element: <PatientDetailPage /> },
      ],
    },
    {
      element: <RequirePermission permission={[PERMISSIONS.VISIT_READ]} />,
      children: [
        { path: paths.visits, element: <VisitsPage /> },
        { path: paths.visit(), element: <VisitDetailPage /> },
      ],
    },
    {
      // Recording a consultation writes a visit and may issue a prescription;
      // the medications section hides itself when the caller may not prescribe.
      element: <RequirePermission permission={[PERMISSIONS.VISIT_WRITE]} />,
      children: [
        // The literal route has to precede the parameterised one.
        { path: paths.consultationNew, element: <ConsultationPage /> },
        { path: paths.consultation(), element: <ConsultationPage /> },
      ],
    },
    {
      element: <RequirePermission permission={[PERMISSIONS.DOCTOR_READ]} />,
      children: [{ path: paths.doctors, element: <DoctorsPage /> }],
    },
    {
      element: <RequirePermission permission={[PERMISSIONS.PRESCRIPTION_READ]} />,
      children: [{ path: paths.clinicDispenses, element: <DispensesPage /> }],
    },
  ],
  // Outside the shell: a sheet is the whole page, so the browser prints paper.
  printRoutes: [
    {
      element: <RequirePermission permission={[PERMISSIONS.PRESCRIPTION_READ]} />,
      children: [{ path: paths.prescriptionPrint(), element: <PrescriptionPrintPage /> }],
    },
    {
      element: <RequirePermission permission={[PERMISSIONS.VISIT_READ]} />,
      children: [{ path: paths.visitPrint(), element: <VisitPrintPage /> }],
    },
  ],
  landing: (viewer) =>
    holdsPermission(viewer.permissions, PERMISSIONS.PATIENT_READ) ? paths.patients : paths.visits,
};
